/**
 * Termination Dialog Component
 *
 * Shows a confirmation dialog when terminating a recruit.
 * Handles Gmail account suspension and email forwarding setup.
 */

import React, { useState, useEffect } from 'react';
import { LwModal, LwField } from '@/components/lw';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Loader2, AlertTriangle, Mail, UserX, Forward } from 'lucide-react';
import { toast } from 'sonner';
import { suspendGmailAccount, getActiveUsersForForwarding } from '@/services/workspaceService';

interface TerminationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  recruit: {
    id: string;
    firstName: string;
    lastName: string;
    gmailAccount: string | null;
  };
  onTerminationComplete: () => void;
}

export const TerminationDialog: React.FC<TerminationDialogProps> = ({
  open,
  onOpenChange,
  recruit,
  onTerminationComplete
}) => {
  const [forwardToUserId, setForwardToUserId] = useState<string>('');
  const [forwardToEmail, setForwardToEmail] = useState<string>('');
  const [activeUsers, setActiveUsers] = useState<Array<{ id: string; email: string; fullName: string }>>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isTerminating, setIsTerminating] = useState(false);

  // Fetch active users for forwarding dropdown
  useEffect(() => {
    const fetchUsers = async () => {
      if (!open) {
        return;
      }
      setIsLoading(true);
      try {
        const users = await getActiveUsersForForwarding();
        setActiveUsers(users);

        // Default to first user (often the admin)
        if (users.length > 0 && !forwardToUserId) {
          setForwardToUserId(users[0].id);
          setForwardToEmail(users[0].email);
        }
      } catch (error) {
        console.error('Error fetching users:', error);
        toast.error('Failed to load users for forwarding');
      } finally {
        setIsLoading(false);
      }
    };

    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch-on-open; default-select guard intentionally excludes forwardToUserId
  }, [open]);

  // Handle user selection change
  const handleUserChange = (userId: string) => {
    setForwardToUserId(userId);
    const user = activeUsers.find(u => u.id === userId);
    if (user) {
      setForwardToEmail(user.email);
    }
  };

  // Handle termination confirmation
  const handleConfirmTermination = async () => {
    if (!recruit.gmailAccount) {
      toast.error('No Gmail account found for this recruit');
      onOpenChange(false);
      onTerminationComplete();
      return;
    }

    if (!forwardToEmail) {
      toast.error('Please select a user to forward emails to');
      return;
    }

    setIsTerminating(true);

    try {
      const result = await suspendGmailAccount(
        recruit.id,
        recruit.gmailAccount,
        forwardToEmail
      );

      if (!result.success) {
        throw new Error(result.error || 'Failed to suspend Gmail account');
      }

      toast.success('Termination completed', {
        description: `Gmail suspended and emails forwarding to ${forwardToEmail}`
      });

      onOpenChange(false);
      onTerminationComplete();
    } catch (error) {
      console.error('Error during termination:', error);
      toast.error('Failed to complete termination', {
        description: error instanceof Error ? error.message : 'Unknown error'
      });
    } finally {
      setIsTerminating(false);
    }
  };

  const fullName = `${recruit.firstName} ${recruit.lastName}`;

  if (!open) {
    return null;
  }

  return (
    <LwModal
      eyebrow={<span style={{ color: 'var(--danger)' }}>Terminate</span>}
      title={`Terminate ${fullName}`}
      sub="This action will suspend the Gmail account and set up email forwarding."
      onClose={() => onOpenChange(false)}
      footer={
        <>
          <button className="btn-sm" onClick={() => onOpenChange(false)} disabled={isTerminating} type="button">
            Cancel
          </button>
          <button
            className="btn-sm danger"
            onClick={handleConfirmTermination}
            disabled={isTerminating || Boolean(recruit.gmailAccount && !forwardToEmail)}
            type="button"
          >
            {isTerminating ? (
              <>
                <Loader2 className="animate-spin" />
                Terminating…
              </>
            ) : (
              <>
                <UserX />
                Confirm Termination
              </>
            )}
          </button>
        </>
      }
    >
      {/* Warning */}
      <div className="err-box">
        <span className="ic"><AlertTriangle /></span>
        <div>
          <div className="err-title">This will:</div>
          <ul className="err-detail" style={{ listStyle: 'disc', paddingLeft: 18, marginTop: 4 }}>
            <li>Suspend Gmail account ({recruit.gmailAccount || 'Not set'})</li>
            <li>Block login immediately</li>
            <li>Forward incoming emails to selected user</li>
          </ul>
        </div>
      </div>

      {/* Gmail Account Info */}
      {recruit.gmailAccount ? (
        <div className="panel" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 12 }}>
          <Mail size={16} style={{ color: 'var(--lw-muted)', flex: 'none' }} />
          <div>
            <p style={{ fontSize: 11, color: 'var(--lw-muted)', margin: 0 }}>Gmail Account</p>
            <p style={{ fontSize: 13, fontFamily: 'ui-monospace, monospace', margin: 0 }}>{recruit.gmailAccount}</p>
          </div>
        </div>
      ) : (
        <div
          style={{
            display: 'flex',
            gap: 10,
            alignItems: 'flex-start',
            background: '#F7F0DC',
            border: '1px solid #E4D2A0',
            borderRadius: 11,
            padding: '12px 14px'
          }}
        >
          <AlertTriangle size={18} style={{ color: '#8A6420', flex: 'none', marginTop: 1 }} />
          <p style={{ fontSize: 12.5, color: '#7E6426', margin: 0 }}>
            No Gmail account found. Termination will proceed without Gmail suspension.
          </p>
        </div>
      )}

      {/* Forward To Selection */}
      {recruit.gmailAccount && (
        <LwField
          label={
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Forward size={14} />
              Forward emails to
            </span>
          }
          htmlFor="forward-to"
          hint="All incoming emails will be forwarded to this user."
        >
          <Select value={forwardToUserId} onValueChange={handleUserChange} disabled={isLoading || isTerminating}>
            <SelectTrigger id="forward-to">
              <SelectValue placeholder="Select user..." />
            </SelectTrigger>
            <SelectContent className="lw-ds">
              {isLoading ? (
                <div style={{ padding: 8, display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--lw-muted)' }}>
                  <Loader2 className="animate-spin" size={16} />
                  Loading users...
                </div>
              ) : activeUsers.length === 0 ? (
                <div style={{ padding: 8, fontSize: 13, color: 'var(--lw-muted)' }}>No active users found</div>
              ) : (
                activeUsers.map(user => (
                  <SelectItem key={user.id} value={user.id}>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <span style={{ fontWeight: 500 }}>{user.fullName}</span>
                      <span style={{ fontSize: 11, color: 'var(--lw-muted)' }}>{user.email}</span>
                    </div>
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
        </LwField>
      )}
    </LwModal>
  );
};
